import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { CardContent, CardHeader, CardTitle } from './ui/card';
import { BarChart3, Briefcase, CreditCard, Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const PAYMENT_COLOR_BY_METHOD = {
  CB: 'bg-blue-500',
  Virement: 'bg-indigo-500',
  Espèce: 'bg-emerald-500'
};

export const ReportSection = () => {
  const { api } = useAuth();
  const [reportPeriod, setReportPeriod] = useState('monthly');
  const [trendMetric, setTrendMetric] = useState('revenue');
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [exportExcelLoading, setExportExcelLoading] = useState(false);
  const [exportPdfLoading, setExportPdfLoading] = useState(false);
  const [selectedTrendIndex, setSelectedTrendIndex] = useState(null);
  const [barDetailLoading, setBarDetailLoading] = useState(false);
  const [barDetail, setBarDetail] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const loadReportData = async () => {
      setReportLoading(true);
      try {
        const response = await api.get(`/reports?period=${reportPeriod}`);
        if (isMounted) {
          setReportData(response.data);
          setSelectedTrendIndex(null);
          setBarDetail(null);
        }
      } catch (error) {
        console.error('Erreur chargement compte rendu:', error);
      } finally {
        if (isMounted) {
          setReportLoading(false);
        }
      }
    };

    loadReportData();
    return () => {
      isMounted = false;
    };
  }, [api, reportPeriod]);

  const services = reportData?.services ?? [];
  const payments = reportData?.payments ?? [];
  const trend = reportData?.trend ?? [];
  const totalAppointments = reportData?.total_appointments ?? 0;
  const totalRevenue = Number(reportData?.total_revenue ?? 0).toFixed(2);

  let maxTrendValue = 0;
  for (let i = 0; i < trend.length; i += 1) {
    const currentValue = trendMetric === 'revenue'
      ? Number(trend[i]?.revenue ?? 0)
      : Number(trend[i]?.appointments ?? 0);
    if (currentValue > maxTrendValue) {
      maxTrendValue = currentValue;
    }
  }

  const renderServiceRows = () => {
    const rows = [];
    for (let i = 0; i < services.length; i += 1) {
      const serviceItem = services[i];
      rows.push(
        <div
          key={`${serviceItem.name}-${i}`}
          className="flex items-center justify-between text-sm"
          data-testid={`report-service-row-${i}`}
        >
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#065F46]" />
            <span className="text-slate-700">{serviceItem.name}</span>
            <span className="text-slate-400">({serviceItem.count})</span>
          </div>
          <span className="font-mono text-slate-900">{Number(serviceItem.revenue).toFixed(2)} €</span>
        </div>
      );
    }
    return rows;
  };

  const renderPaymentRows = () => {
    const rows = [];
    for (let i = 0; i < payments.length; i += 1) {
      const paymentItem = payments[i];
      const paymentClass = PAYMENT_COLOR_BY_METHOD[paymentItem.method] || 'bg-slate-500';
      rows.push(
        <div
          key={`${paymentItem.method}-${i}`}
          className="flex items-center justify-between text-sm"
          data-testid={`report-payment-row-${i}`}
        >
          <div className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${paymentClass}`} />
            <span className="text-slate-700">{paymentItem.method}</span>
            <span className="text-slate-400">({paymentItem.count})</span>
          </div>
          <span className="font-mono text-slate-900">{Number(paymentItem.revenue).toFixed(2)} €</span>
        </div>
      );
    }
    return rows;
  };

  const renderTrendBars = () => {
    const bars = [];
    for (let i = 0; i < trend.length; i += 1) {
      const trendItem = trend[i];
      const revenueValue = Number(trendItem.revenue ?? 0);
      const appointmentsValue = Number(trendItem.appointments ?? 0);
      const currentValue = trendMetric === 'revenue' ? revenueValue : appointmentsValue;
      const barHeight = maxTrendValue > 0 ? Math.max((currentValue / maxTrendValue) * 100, 6) : 0;
      const valueLabel = trendMetric === 'revenue' ? `${currentValue.toFixed(0)}€` : `${currentValue}`;
      const isActive = selectedTrendIndex === i;
      bars.push(
        <div
          key={`${trendItem.label}-${i}`}
          className="flex-1 min-w-0 flex flex-col items-center gap-1"
          data-testid={`report-trend-point-${i}`}
        >
          <p className="text-[10px] text-slate-500 font-mono" data-testid={`report-trend-value-${i}`}>{valueLabel}</p>
          <div className="h-24 w-full flex items-end justify-center px-1">
            <div
              className={`w-full max-w-6 rounded-t-md bg-gradient-to-t from-[#065F46] to-[#10B981] transition-all duration-300 cursor-pointer ${isActive ? 'ring-2 ring-emerald-300' : ''}`}
              style={{ height: `${barHeight}%` }}
              data-testid={`report-trend-bar-${i}`}
              title={`${trendItem.label} • ${revenueValue.toFixed(2)}€ • ${appointmentsValue} RDV`}
              onClick={() => handleSelectTrendBar(i)}
            />
          </div>
          <p className="text-[10px] text-slate-500" data-testid={`report-trend-label-${i}`}>{trendItem.label}</p>
        </div>
      );
    }
    return bars;
  };

  const handleSelectTrendBar = async (index) => {
    setSelectedTrendIndex(index);
    setBarDetailLoading(true);
    try {
      const response = await api.get(`/reports/bar-details?period=${reportPeriod}&index=${index}`);
      setBarDetail(response.data);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erreur lors du chargement du détail de période');
      setBarDetail(null);
    } finally {
      setBarDetailLoading(false);
    }
  };

  // Auto-select bar corresponding to "today" once trend data is loaded
  useEffect(() => {
    if (!trend || trend.length === 0 || selectedTrendIndex !== null || barDetailLoading) {
      return;
    }
    const now = new Date();
    let idx;
    if (reportPeriod === 'daily') {
      idx = now.getHours();
    } else if (reportPeriod === 'weekly') {
      idx = (now.getDay() + 6) % 7;
    } else if (reportPeriod === 'monthly') {
      idx = Math.floor((now.getDate() - 1) / 7);
    } else if (reportPeriod === 'yearly') {
      idx = now.getMonth();
    } else {
      idx = trend.length - 1;
    }
    if (idx < 0 || idx >= trend.length) {
      idx = trend.length - 1;
    }
    handleSelectTrendBar(idx);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trend]);

  const triggerBrowserDownload = (blobData, filename) => {
    const url = window.URL.createObjectURL(new Blob([blobData]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleExportExcel = async () => {
    setExportExcelLoading(true);
    try {
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[-:T]/g, '');
      const excelResponse = await api.get('/reports/export/excel', { responseType: 'blob' });
      triggerBrowserDownload(excelResponse.data, `compte_rendu_complet_${timestamp}.xlsx`);
      toast.success('Export Excel téléchargé');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erreur lors de l’export Excel');
    } finally {
      setExportExcelLoading(false);
    }
  };

  const handleExportPdfDetailed = async () => {
    setExportPdfLoading(true);
    try {
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[-:T]/g, '');
      const pdfResponse = await api.get('/reports/export/pdf', {
        responseType: 'blob',
        params: {
          include_tables: true
        }
      });
      triggerBrowserDownload(pdfResponse.data, `compte_rendu_detaille_${timestamp}.pdf`);
      toast.success('Export PDF détaillé téléchargé');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erreur lors de l’export PDF détaillé');
    } finally {
      setExportPdfLoading(false);
    }
  };

  return (
    <div className="h-full" data-testid="report-section">
      <CardHeader className="border-b border-slate-100 pb-4">
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="font-outfit text-lg" data-testid="report-title">Compte rendu</CardTitle>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              className="border-slate-300 hover:bg-slate-50"
              onClick={handleExportExcel}
              disabled={exportExcelLoading}
              data-testid="report-export-excel-button"
            >
              {exportExcelLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Exporter Excel
            </Button>
            <Button
              type="button"
              className="bg-[#065F46] hover:bg-[#047857] text-white"
              onClick={handleExportPdfDetailed}
              disabled={exportPdfLoading}
              data-testid="report-export-pdf-button"
            >
              {exportPdfLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              PDF détaillé
            </Button>
          </div>
        </div>
        <div className="flex gap-1 mt-3 bg-slate-100 p-1 rounded-lg" data-testid="report-period-selector">
          <button
            onClick={() => setReportPeriod('weekly')}
            className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${reportPeriod === 'weekly' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600'}`}
            data-testid="period-weekly"
          >
            Semaine
          </button>
          <button
            onClick={() => setReportPeriod('monthly')}
            className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${reportPeriod === 'monthly' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600'}`}
            data-testid="period-monthly"
          >
            Mois
          </button>
          <button
            onClick={() => setReportPeriod('yearly')}
            className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${reportPeriod === 'yearly' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600'}`}
            data-testid="period-yearly"
          >
            Année
          </button>
        </div>
      </CardHeader>

      <CardContent className="p-4">
        {reportLoading ? (
          <div className="flex items-center justify-center py-8" data-testid="report-loading">
            <Loader2 className="w-6 h-6 animate-spin text-[#065F46]" />
          </div>
        ) : (
          <div className="space-y-5" data-testid="report-content">
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-[#065F46]/5 rounded-lg p-3" data-testid="report-total-appointments-card">
                <p className="text-xs text-slate-500 mb-1">RDV</p>
                <p className="text-xl font-bold text-slate-900" data-testid="report-total-appointments-value">{totalAppointments}</p>
              </div>
              <div className="bg-[#D97706]/5 rounded-lg p-3" data-testid="report-total-revenue-card">
                <p className="text-xs text-slate-500 mb-1">Chiffre d'affaires</p>
                <p className="text-xl font-bold font-mono text-[#065F46]" data-testid="report-total-revenue-value">{totalRevenue} €</p>
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 bg-gradient-to-b from-emerald-50/60 to-white p-3" data-testid="report-trend-chart-card">
              <div className="flex items-center justify-between gap-2 mb-3">
                <div className="flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-slate-500" />
                  <p className="text-xs font-medium text-slate-600 uppercase" data-testid="report-trend-chart-title">
                    {trendMetric === 'revenue' ? 'Évolution du CA' : 'Évolution des RDV'}
                  </p>
                </div>
                <div className="flex items-center gap-1 p-1 bg-slate-100 rounded-md" data-testid="report-trend-metric-switch">
                  <button
                    type="button"
                    className={`px-2 py-1 text-[10px] rounded transition-colors ${trendMetric === 'revenue' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                    onClick={() => setTrendMetric('revenue')}
                    data-testid="report-trend-metric-revenue"
                  >
                    CA
                  </button>
                  <button
                    type="button"
                    className={`px-2 py-1 text-[10px] rounded transition-colors ${trendMetric === 'appointments' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                    onClick={() => setTrendMetric('appointments')}
                    data-testid="report-trend-metric-appointments"
                  >
                    RDV
                  </button>
                </div>
              </div>
              {trend.length > 0 ? (
                <div className="flex items-end gap-1" data-testid="report-trend-chart">
                  {renderTrendBars()}
                </div>
              ) : (
                <p className="text-sm text-slate-400 italic" data-testid="report-empty-trend">Aucune donnée</p>
              )}

              {barDetailLoading && (
                <div className="mt-3 py-4 flex items-center justify-center" data-testid="report-bar-detail-loading">
                  <Loader2 className="w-5 h-5 animate-spin text-[#065F46]" />
                </div>
              )}

              {!barDetailLoading && barDetail && (
                <div className="mt-3 border border-slate-200 rounded-md bg-white p-3 space-y-3" data-testid="report-bar-detail-card">
                  <div>
                    <p className="text-xs uppercase text-slate-500" data-testid="report-bar-detail-title">Détail période {barDetail.label}</p>
                    <p className="text-xs text-slate-400" data-testid="report-bar-detail-range">
                      {new Date(barDetail.start_date).toLocaleDateString('fr-FR')} - {new Date(barDetail.end_date).toLocaleDateString('fr-FR')}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-slate-50 rounded p-2" data-testid="report-bar-detail-appointments-card">
                      <p className="text-xs text-slate-500">RDV</p>
                      <p className="font-semibold text-slate-900" data-testid="report-bar-detail-appointments-value">{barDetail.total_appointments}</p>
                    </div>
                    <div className="bg-slate-50 rounded p-2" data-testid="report-bar-detail-revenue-card">
                      <p className="text-xs text-slate-500">CA</p>
                      <p className="font-semibold text-[#065F46]" data-testid="report-bar-detail-revenue-value">{Number(barDetail.total_revenue || 0).toFixed(2)} €</p>
                    </div>
                  </div>

                  <div className="space-y-1" data-testid="report-bar-detail-payments-list">
                    <p className="text-xs uppercase text-slate-500">Type de client</p>
                    {(barDetail.client_types || []).slice(0, 6).map((clientType, index) => (
                      <div key={`${clientType.type}-${index}`} className="text-xs flex items-center justify-between gap-2" data-testid={`report-bar-detail-client-type-row-${index}`}>
                        <span className="text-slate-700">{clientType.type} ({clientType.count})</span>
                        <span className="font-mono text-slate-900">{Number(clientType.revenue || 0).toFixed(2)} €</span>
                      </div>
                    ))}
                    {(barDetail.client_types || []).length === 0 && (
                      <p className="text-xs text-slate-400 italic" data-testid="report-bar-detail-client-types-empty">Aucun type de client sur cette période</p>
                    )}

                    <p className="text-xs uppercase text-slate-500">Modes de paiement</p>
                    {(barDetail.payments || []).slice(0, 6).map((payment, index) => (
                      <div key={`${payment.method}-${index}`} className="text-xs flex items-center justify-between gap-2" data-testid={`report-bar-detail-payment-row-${index}`}>
                        <span className="text-slate-700">{payment.method} ({payment.count})</span>
                        <span className="font-mono text-slate-900">{Number(payment.revenue || 0).toFixed(2)} €</span>
                      </div>
                    ))}
                    {(barDetail.payments || []).length === 0 && (
                      <p className="text-xs text-slate-400 italic" data-testid="report-bar-detail-payments-empty">Aucun mode de paiement sur cette période</p>
                    )}

                    <p className="text-xs uppercase text-slate-500">Prestataires</p>
                    {(barDetail.providers || []).slice(0, 6).map((provider, index) => (
                      <div key={`${provider.name}-${index}`} className="text-xs flex items-center justify-between gap-2" data-testid={`report-bar-detail-provider-row-${index}`}>
                        <span className="text-slate-700">{provider.name} ({provider.count})</span>
                        <span className="font-mono text-slate-900">{Number(provider.revenue || 0).toFixed(2)} €</span>
                      </div>
                    ))}
                    {(barDetail.providers || []).length === 0 && (
                      <p className="text-xs text-slate-400 italic" data-testid="report-bar-detail-providers-empty">Aucun prestataire sur cette période</p>
                    )}
                  </div>

                  <div className="space-y-1" data-testid="report-bar-detail-appointments-list">
                    {(barDetail.appointments || []).slice(0, 12).map((apt, index) => (
                      <div key={`${apt.id}-${index}`} className="text-xs flex items-center justify-between gap-2 border-b border-slate-100 py-1" data-testid={`report-bar-detail-appointment-row-${index}`}>
                        <span className="text-slate-700 truncate">
                          {apt.client_name} • {apt.service_type}
                        </span>
                        <span className="font-mono text-slate-900">{Number(apt.price || 0).toFixed(2)} €</span>
                      </div>
                    ))}
                    {(barDetail.appointments || []).length === 0 && (
                      <p className="text-xs text-slate-400 italic" data-testid="report-bar-detail-empty">Aucune donnée sur cette période</p>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div data-testid="report-services-breakdown">
              <div className="flex items-center gap-2 mb-2">
                <Briefcase className="w-4 h-4 text-slate-400" />
                <p className="text-xs font-medium text-slate-600 uppercase">Par service</p>
              </div>
              {services.length > 0 ? (
                <div className="space-y-2">{renderServiceRows()}</div>
              ) : (
                <p className="text-sm text-slate-400 italic" data-testid="report-empty-services">Aucune donnée</p>
              )}
            </div>

            <div data-testid="report-payments-breakdown">
              <div className="flex items-center gap-2 mb-2">
                <CreditCard className="w-4 h-4 text-slate-400" />
                <p className="text-xs font-medium text-slate-600 uppercase">Par paiement</p>
              </div>
              {payments.length > 0 ? (
                <div className="space-y-2">{renderPaymentRows()}</div>
              ) : (
                <p className="text-sm text-slate-400 italic" data-testid="report-empty-payments">Aucune donnée</p>
              )}
            </div>
          </div>
        )}
      </CardContent>

    </div>
  );
};
